import type { PdfFillRect, PdfPageContent, PdfTextItem } from '@construction/shared';

/**
 * 从 PDF 里取出每一页的文字（带坐标）和实心色块（CR-080）。
 *
 * 取出来交给 shared 的 `parseCalendarSchedule` 按版式还原成一项一项 —— 这里只管「PDF 里画了什么」，
 * 不管它是什么版式。解析放在浏览器里做：pdfjs 本来就在（看图用），服务器不用再装一套、不用读大文件。
 *
 * 色块从绘图指令里取：pdfjs 把每一次 `fill` 前的路径交给我们，带着包围盒（minMax）。
 * 包围盒在当时的坐标系里，所以要跟着 save / restore / transform 维护一份当前变换矩阵，
 * 最后换算到和文字同一个坐标系（PDF 用户空间，y 向上）。
 *
 * **只在点「解析并核对」时才加载**：pdfjs 一兆多，不能进首屏。
 */

/** 超过这么多页就不读了：进度表几页到几十页，上百页的多半传错了文件 */
const MAX_PAGES = 60;

type Matrix = [number, number, number, number, number, number];

const multiply = (m: Matrix, n: readonly number[]): Matrix => [
  m[0] * n[0]! + m[2] * n[1]!,
  m[1] * n[0]! + m[3] * n[1]!,
  m[0] * n[2]! + m[2] * n[3]!,
  m[1] * n[2]! + m[3] * n[3]!,
  m[0] * n[4]! + m[2] * n[5]! + m[4],
  m[1] * n[4]! + m[3] * n[5]! + m[5],
];
const apply = (m: Matrix, x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];

export interface ExtractedPdf {
  pages: PdfPageContent[];
  /** 文件的生成日期（`YYYY-MM-DD`），推年份用 */
  createdAt: string | null;
}

export async function extractPdfContent(url: string): Promise<ExtractedPdf | null> {
  const { pdfjs } = await import('@/features/drawings/pdfjs');
  const OPS = pdfjs.OPS as Record<string, number>;
  const doc = await pdfjs.getDocument({ url }).promise;
  try {
    if (doc.numPages > MAX_PAGES) return null;

    const meta = await doc.getMetadata().catch(() => null);
    const info = (meta?.info ?? {}) as { CreationDate?: string };
    const created = /D:(\d{4})(\d{2})(\d{2})/.exec(info.CreationDate ?? '');

    const pages: PdfPageContent[] = [];
    for (let n = 1; n <= doc.numPages; n++) {
      const page = await doc.getPage(n);

      const content = await page.getTextContent();
      const texts: PdfTextItem[] = [];
      for (const item of content.items) {
        if (!('str' in item) || !item.str.trim()) continue;
        texts.push({ s: item.str, x: item.transform[4] as number, y: item.transform[5] as number, w: item.width });
      }

      const ops = await page.getOperatorList();
      const rects: PdfFillRect[] = [];
      let state = { ctm: [1, 0, 0, 1, 0, 0] as Matrix, fill: '#000000' };
      const stack: Array<typeof state> = [];
      for (let i = 0; i < ops.fnArray.length; i++) {
        const fn = ops.fnArray[i];
        const args = ops.argsArray[i] as unknown[];
        if (fn === OPS.save) stack.push({ ...state });
        else if (fn === OPS.restore) state = stack.pop() ?? state;
        else if (fn === OPS.transform) state.ctm = multiply(state.ctm, args as number[]);
        else if (fn === OPS.setFillRGBColor) state.fill = String(args[0]);
        else if (fn === OPS.constructPath && args[0] === OPS.fill) {
          // pdfjs 5：[结束这条路径的操作, [路径数据], 包围盒 minX minY maxX maxY]
          const box = args[2] as ArrayLike<number> | null;
          if (!box || box.length < 4) continue;
          const a = apply(state.ctm, box[0]!, box[1]!);
          const b = apply(state.ctm, box[2]!, box[3]!);
          rects.push({
            fill: state.fill,
            x0: Math.min(a[0], b[0]),
            x1: Math.max(a[0], b[0]),
            y0: Math.min(a[1], b[1]),
            y1: Math.max(a[1], b[1]),
          });
        }
      }
      pages.push({ texts, rects });
      page.cleanup();
    }

    return { pages, createdAt: created ? `${created[1]}-${created[2]}-${created[3]}` : null };
  } finally {
    void doc.destroy();
  }
}
